import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import Header from './component/Header'
import Footer from './component/Footer'
import LoadingOverlay from './component/LoadingOverlay'
import VideoModal from './component/VideoModal'
import NewsDrawer from './component/NewsDrawer'
import GameFeatures from './component/GameFeatures'
import BookBanner from './component/BookBanner'
import { LANG_CONFIG, getInitLang } from './config/lang.config'
import {
  getBannerList,
  getHomeConfig,
  getContentConfig,
  loadNewsList
} from './config/api'

import '../../common/util/polyfill'
import './style/common.scss'
import './style/index.scss'

const SECTION_HASHES = ['home', 'features', 'gallery']
const HASH_TO_INDEX = { home: 0, features: 1, gallery: 2 }

const syncSectionHash = index => {
  const hash = SECTION_HASHES[index]
  if (hash && `#${hash}` !== window.location.hash) {
    window.history.replaceState(null, '', `#${hash}`)
  }
}

const getIndexFromHash = () => {
  const key = (window.location.hash || '').replace(/^#/, '')
  const idx = HASH_TO_INDEX[key]
  return typeof idx === 'number' ? idx : 0
}

function App() {
  const [lang, setLang] = useState(getInitLang())
  const config = LANG_CONFIG[lang]

  const [loadingVisible, setLoadingVisible] = useState(true)
  const [loadingProgress, setLoadingProgress] = useState(0)

  const [videoDialogVisible, setVideoDialogVisible] = useState(false)
  const [currentVideoUrl, setCurrentVideoUrl] = useState('')

  const [bannerList, setBannerList] = useState([])
  const [homeConfig, setHomeConfig] = useState({})
  const [contentConfig, setContentConfig] = useState({})

  const [pageIndex, setPageIndex] = useState(0)
  const pageSwiperRef = useRef(null)

  const [isNewsOpen, setIsNewsOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState('latest')
  const [newsList, setNewsList] = useState([])

  const playVideo = url => {
    if (!url) return
    setCurrentVideoUrl(url)
    setVideoDialogVisible(true)
  }

  const handleToggleNews = () => {
    setIsNewsOpen(prev => !prev)
  }

  const handleCategoryChange = cat => {
    setActiveCategory(cat)
  }

  const handleSwitchLang = target => {
    if (target === lang) return
    setLang(target)
    const sp = new URLSearchParams(window.location.search)
    if (target === 'en') sp.set('lang', 'en')
    else sp.delete('lang')
    const qs = sp.toString()
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname)
  }

  const handleNavClick = index => {
    setPageIndex(index)
    if (pageSwiperRef.current) {
      pageSwiperRef.current.slideTo(index)
    }
  }

  const handleScrollToNext = () => {
    if (pageSwiperRef.current) {
      pageSwiperRef.current.slideNext()
    }
  }

  // Load News List
  useEffect(() => {
    loadNewsList({ lang, category: activeCategory, perPage: 5, page: 1 })
      .then(res => {
        const { code, data } = res.data || {}
        if (code === 0 && data) {
          setNewsList(data.data || [])
        } else {
          setNewsList([])
        }
      })
      .catch(() => {
        setNewsList([])
      })
  }, [lang, activeCategory])

  // Apply Page Title & Meta
  useEffect(() => {
    document.title = config.title
    const kw = document.getElementById('meta-keywords')
    const desc = document.getElementById('meta-description')
    if (kw) kw.setAttribute('content', config.keywords)
    if (desc) desc.setAttribute('content', config.description)
  }, [config])

  // Load Main CMS Data
  useEffect(() => {
    const pHome = getHomeConfig(config.apiHome)
      .then(res => {
        const { data = [], code } = res.data || {}
        if (code === 0) {
          const { data: list } = data || {}
          if (list?.length) {
            setHomeConfig(list[0]?.content || {})
          }
        }
      })
      .catch(() => {})

    const pContent = getContentConfig(config.apiContent)
      .then(res => {
        const { data = [], code } = res.data || {}
        if (code === 0) {
          const [content] = data?.data || []
          setContentConfig(content?.content || {})
        }
      })
      .catch(() => {})

    const pBanner = getBannerList(config.apiBanner)
      .then(res => {
        const { data = [], code } = res.data || {}
        if (code === 0) {
          const { data: list } = data || {}
          setBannerList(list?.map(item => item.content) || [])
        }
      })
      .catch(() => {})

    const progressTimer = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev < 90) {
          const nextVal = prev + Math.floor(Math.random() * 5) + 2
          return nextVal > 90 ? 90 : nextVal
        }
        return 90
      })
    }, 50)

    Promise.allSettled([pHome, pContent, pBanner]).then(() => {
      clearInterval(progressTimer)
      setLoadingProgress(100)
      setTimeout(() => setLoadingVisible(false), 300)
    })

    return () => clearInterval(progressTimer)
  }, [lang, config.apiHome, config.apiContent, config.apiBanner])

  // Init Page Swiper
  useEffect(() => {
    if (window.Swiper && !pageSwiperRef.current) {
      const swiper = new window.Swiper('.page-swiper', {
        direction: 'vertical',
        allowTouchMove: false,
        watchSlidesProgress: false,
        speed: 800,
        slidesPerView: 'auto',
        centeredSlides: false,
        loop: false,
        autoplay: false,
        clicks: { preventClicks: true },
        mousewheel: true,
        on: {
          slideChange: function () {
            setPageIndex(this.realIndex)
            syncSectionHash(this.realIndex)
          }
        }
      })
      pageSwiperRef.current = swiper

      const targetIdx = getIndexFromHash()
      if (targetIdx !== 0) {
        swiper.slideTo(targetIdx, 0)
      }
    }
  }, [])

  return (
    <div id="app" data-lang={lang}>
      <LoadingOverlay
        visible={loadingVisible}
        progress={loadingProgress}
        tips={config.loadingText}
      />

      <Header
        lang={lang}
        config={config}
        homeConfig={homeConfig}
        pageIndex={pageIndex}
        onNavClick={handleNavClick}
        onSwitchLang={handleSwitchLang}
      />

      <section className="page-swiper">
        <section className="swiper-wrapper page-content">
          {/* Section 1: Hero */}
          <section
            id="s1"
            className="swiper-slide"
            style={
              homeConfig.index_bg_url || homeConfig.index_bg
                ? { backgroundImage: `url(${homeConfig.index_bg_url || homeConfig.index_bg})` }
                : undefined
            }
          >
            <div className={config.showTapTap ? 'btns-subscribe' : 'btn-subscribe-wrap'}>
              {homeConfig.video ? (
                <div
                  className={`btn-play ${videoDialogVisible ? 'pause' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Play video"
                  onClick={() => playVideo(homeConfig.video)}
                />
              ) : null}
              {homeConfig.url ? (
                <a href={homeConfig.url} target="_blank" rel="noopener noreferrer" className="btn-subscribe">
                  <img src={homeConfig.img} alt="订阅" />
                </a>
              ) : null}
              {config.showTapTap ? (
                <a href={config.tapUrl} target="_blank" rel="noopener noreferrer" className="btn-tap">
                  <img src={`${config.imgBase}btn_tap.png`} alt="TapTap" />
                </a>
              ) : null}
            </div>

            <NewsDrawer
              isOpen={isNewsOpen}
              onToggle={handleToggleNews}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              newsList={newsList}
              config={config}
              lang={lang}
            />
          </section>

          {/* Section 2: Features */}
          <GameFeatures
            contentConfig={contentConfig}
            config={config}
            onPlayVideo={playVideo}
          />

          {/* Section 3: Book Banner */}
          <BookBanner bannerList={bannerList} config={config} />
        </section>
      </section>

      {pageIndex < 2 ? (
        <button
          type="button"
          className="scroll-down-tips"
          onClick={handleScrollToNext}
          aria-label="向下滚动"
        >
          <img src="./images/swiper_scroll.png" alt="向下滚动" />
        </button>
      ) : null}

      <VideoModal
        visible={videoDialogVisible}
        videoUrl={currentVideoUrl}
        onClose={() => setVideoDialogVisible(false)}
      />

      <Footer config={config} />
    </div>
  )
}

ReactDOM.render(<App />, document.getElementById('app'))
